import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { io } from "socket.io-client";
import axios from "axios";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../styles/MobileUpload.css";
import spectraLogo from "../static/img/Spectra Plasmonics logo.webp";

const SERVER_MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5MB server-side limit

/**
 * MobileUpload Component
 * Mobile-optimized page for scanning QR code and uploading images
 * Users can take photos directly from their phone camera
 */
const MobileUpload = () => {
  const { sessionId } = useParams();
  const apiBaseUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [maxImages, setMaxImages] = useState(3);
  const [currentImageCount, setCurrentImageCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isDesktopConnected, setIsDesktopConnected] = useState(false);
  const [hasEverConnected, setHasEverConnected] = useState(false);
  const [hasEverDesktopConnected, setHasEverDesktopConnected] = useState(false);
  const [showDisconnectAlert, setShowDisconnectAlert] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);

  // PIN verification state
  const [pinVerified, setPinVerified] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [verifyingPin, setVerifyingPin] = useState(false);

  // Track socket ref to prevent duplicate connections and ensure proper cleanup
  const socketRef = useRef(null);
  const connectionAttemptRef = useRef(false);

  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const MAX_IMAGE_DIMENSION = 1600;
  const IMAGE_UPLOAD_QUALITY = 0.82;
  const UPLOAD_REQUEST_TIMEOUT_MS = 60000;

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = () =>
        reject(new Error(`Failed to read file: ${file.name}`));
      reader.readAsDataURL(file);
    });

  const loadImageElement = (file) =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Failed to decode image: ${file.name}`));
      };

      img.src = objectUrl;
    });

  const compressImageForUpload = async (file) => {
    if (!file.type || !file.type.startsWith("image/")) {
      return readFileAsDataUrl(file);
    }

    const img = await loadImageElement(file);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return readFileAsDataUrl(file);
    }

    const originalWidth = img.naturalWidth || img.width;
    const originalHeight = img.naturalHeight || img.height;

    if (!originalWidth || !originalHeight) {
      return readFileAsDataUrl(file);
    }

    const maxSide = Math.max(originalWidth, originalHeight);
    const scale =
      maxSide > MAX_IMAGE_DIMENSION ? MAX_IMAGE_DIMENSION / maxSide : 1;
    const targetWidth = Math.max(1, Math.round(originalWidth * scale));
    const targetHeight = Math.max(1, Math.round(originalHeight * scale));

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    const compressedDataUrl = canvas.toDataURL(
      "image/jpeg",
      IMAGE_UPLOAD_QUALITY,
    );

    canvas.width = 1;
    canvas.height = 1;

    if (!compressedDataUrl || compressedDataUrl.length === 0) {
      return readFileAsDataUrl(file);
    }

    // If compression makes payload larger, keep original encoding.
    if (compressedDataUrl.length >= file.size * 1.4) {
      return readFileAsDataUrl(file);
    }

    return compressedDataUrl;
  };

  // Initialize WebSocket connection
  useEffect(() => {
    // Validate sessionId before attempting connection
    if (!sessionId || sessionId.trim() === "") {
      setError("Invalid session ID. Please generate a new QR code.");
      connectionAttemptRef.current = false;
      return;
    }

    // Prevent creating multiple simultaneous connection attempts
    // This guards against rapid mount/unmount cycles
    if (connectionAttemptRef.current || socketRef.current?.connected) {
      return;
    }

    connectionAttemptRef.current = true;

    // Clean up any existing socket before creating a new one
    // This prevents zombie connections from accumulating
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const newSocket = io(
      process.env.REACT_APP_API_URL || window.location.origin,
      {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        // Keep trying on flaky mobile/cloud networks instead of giving up quickly.
        reconnectionAttempts: Infinity,
        auth: {
          sessionId: sessionId, // Use sessionId as bearer token (no user login required)
        },
      },
    );

    newSocket.on("connect", () => {
      setIsConnected(true);
      setHasEverConnected(true);
      // Register this mobile session with the server
      newSocket.emit("register-mobile", { sessionId });
    });

    newSocket.on("image-count-update", (data) => {
      setCurrentImageCount(data.currentCount || 0);
      setMaxImages(data.maxImages || 3);
      // Server sends session expiration timestamp
      if (data.expiresAt) {
        setSessionExpiresAt(data.expiresAt);
      }
    });

    newSocket.on("desktop-connected", () => {
      setIsDesktopConnected(true);
      setHasEverDesktopConnected(true);
    });

    newSocket.on("desktop-disconnected", () => {
      setIsDesktopConnected(false);
    });

    newSocket.on("pin-verified", () => {
      console.log("PIN verified successfully");
      setPinVerified(true);
      setPinError("");
      setSuccess("PIN verified! You can now upload images.");
      setTimeout(() => setSuccess(""), 3000);
    });

    newSocket.on("pin-verification-failed", (data) => {
      console.log("PIN verification failed:", data);
      setVerifyingPin(false);
      setPinError(data.message || "Incorrect PIN");

      const attemptsRemaining = Number(data?.attemptsRemaining);
      const shouldExpireSession =
        Boolean(data?.maxAttemptsExceeded) ||
        (!Number.isNaN(attemptsRemaining) && attemptsRemaining <= 0);

      if (shouldExpireSession) {
        setSessionExpired(true);
        setError(
          "Maximum PIN attempts exceeded. Please generate a new QR code.",
        );
        setPinInput("");
      }
    });

    newSocket.on("session-expired", () => {
      setSessionExpired(true);
      setError(
        "Mobile upload session has expired. Please generate a new QR code, then scan the new code to continue uploading.",
      );
      setLoading(false);
      // Disconnect socket since session is no longer valid
      newSocket.disconnect();
    });

    newSocket.on("disconnect", () => {
      setIsConnected(false);
      // Do not infer desktop disconnect from a mobile transport drop.
      // Desktop connectivity is tracked via explicit server events.
      connectionAttemptRef.current = false;
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      // Ensure socket is properly disconnected on unmount
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      connectionAttemptRef.current = false;
    };
  }, [sessionId]);

  const shouldShowDisconnect =
    !sessionExpired &&
    hasEverConnected &&
    hasEverDesktopConnected &&
    isConnected &&
    !isDesktopConnected;

  const topStatusAlert = sessionExpired
    ? {
        variant: "warning",
        title: "Session expired:",
        body:
          error ||
          "This upload session is no longer valid. Please generate a new QR code and PIN on the desktop app, then scan again.",
        footer:
          "Please return to the desktop app and start a new upload session.",
      }
    : showDisconnectAlert
      ? {
          variant: "error",
          title: "Disconnected:",
          body: "The desktop app is no longer connected. Please return to the desktop app and start a new upload session.",
          footer: null,
        }
      : null;

  useEffect(() => {
    let timerId;
    if (shouldShowDisconnect) {
      // Debounce disconnect UI to avoid flashing during brief reconnects.
      timerId = setTimeout(() => setShowDisconnectAlert(true), 1200);
    } else {
      setShowDisconnectAlert(false);
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [shouldShowDisconnect]);

  // Timer to countdown session expiry
  useEffect(() => {
    if (sessionExpired) {
      setTimeRemaining(0);
      return undefined;
    }

    // Use server-provided expiration time if available
    if (!sessionExpiresAt) {
      // Wait for server expiration timestamp instead of showing a reset value.
      setTimeRemaining(null);
      return undefined;
    }

    // Calculate remaining time based on server's expiration timestamp
    const updateTimer = () => {
      const now = Date.now();
      const remaining = sessionExpiresAt - now;

      if (remaining <= 0) {
        setTimeRemaining(0);
      } else {
        setTimeRemaining(remaining);
      }
    };

    // Update immediately
    updateTimer();

    // Then update every second
    const timer = setInterval(updateTimer, 1000);

    return () => clearInterval(timer);
  }, [sessionExpired, sessionExpiresAt]);

  const formatTimeRemaining = (ms) => {
    if (ms == null) return "--:--";
    if (ms <= 0) return "Expired";
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  /**
   * Handle PIN verification
   */
  const handleVerifyPin = (e) => {
    e.preventDefault();

    if (!pinInput || pinInput.length !== 6) {
      setPinError("Please enter a 6-digit PIN");
      return;
    }

    if (!socket || !isConnected) {
      setPinError("Not connected to server. Please refresh and try again.");
      return;
    }

    setVerifyingPin(true);
    setPinError("");

    socket.emit("verify-pin", {
      sessionId,
      pin: pinInput,
    });
  };

  /**
   * Handle PIN input change - only allow digits
   */
  const handlePinInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Remove non-digits
    if (value.length <= 6) {
      setPinInput(value);
      setPinError("");
    }
  };

  /**
   * Handle file selection from input
   * Process all selected files and upload to S3
   */
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Check if we've reached max images
    const slotsRemaining = maxImages - currentImageCount;
    if (slotsRemaining <= 0) {
      setError(`Maximum ${maxImages} images reached. Cannot upload more.`);
      e.target.value = "";
      return;
    }

    // Limit files to remaining slots
    const filesToUpload = files.slice(0, slotsRemaining);
    if (files.length > slotsRemaining) {
      setError(
        `Only ${slotsRemaining} slot(s) remaining. Uploading first ${slotsRemaining} image(s).`,
      );
    }

    // Validate ALL files upfront before attempting any uploads
    const validationErrors = [];
    const validFiles = [];

    for (const file of filesToUpload) {
      if (file.size > MAX_FILE_SIZE) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(2);
        validationErrors.push(`${file.name} (${sizeMB}MB exceeds 5MB limit)`);
      } else {
        validFiles.push(file);
      }
    }

    // If all files failed validation, show error and stop
    if (validFiles.length === 0) {
      setError(
        `All selected files are too large: ${validationErrors.join(", ")}`,
      );
      e.target.value = "";
      return;
    }

    // If some files failed validation, warn user but continue with valid files
    if (validationErrors.length > 0) {
      setError(
        `Skipping ${validationErrors.length} oversized file(s): ${validationErrors.join(", ")}`,
      );
    } else {
      setError("");
    }

    setLoading(true);
    setSuccess("");

    const uploadResults = {
      successful: [],
      failed: [],
      totalAttempted: validFiles.length,
    };

    let runningCount = uploadedCount;

    // Process all valid files, continuing even if some fail
    for (const file of validFiles) {
      let payloadForUpload;
      try {
        payloadForUpload = await compressImageForUpload(file);

        // Enforce server max size based on compressed payload size (~5MB limit)
        if (typeof payloadForUpload === "string") {
          // Base64-encoded data: each 4 chars represent ~3 bytes
          const approxBytes = Math.floor((payloadForUpload.length * 3) / 4);
          if (approxBytes > SERVER_MAX_UPLOAD_BYTES) {
            uploadResults.failed.push(
              `${file.name} (File is too large even after compression; please try again with a lower-resolution photo under 5MB.)`,
            );
            continue;
          }
        }

        const response = await axios.post(
          apiBaseUrl ? `${apiBaseUrl}/api/mobile-upload` : "/api/mobile-upload",
          {
            sessionId,
            image: payloadForUpload,
            fileName: file.name,
          },
          {
            timeout: UPLOAD_REQUEST_TIMEOUT_MS,
          },
        );

        if (response.data.success) {
          runningCount += 1;
          setUploadedCount(runningCount);
          uploadResults.successful.push(file.name);

          // Notify desktop app via WebSocket
          if (socket) {
            socket.emit("image-uploaded", {
              sessionId,
              imageUrl: response.data.imageUrl,
              imageKey: response.data.imageKey,
              fileName: file.name,
              uploadCount: runningCount,
            });
          }
        }
      } catch (err) {
        console.error(`Error processing ${file.name}:`, err);
        const serverMessage = err.response?.data?.error;
        const timeoutMessage =
          err.code === "ECONNABORTED"
            ? "Upload timed out. Please try again with a clearer/lower-resolution photo."
            : null;
        const transportMessage =
          !serverMessage && !timeoutMessage
            ? "Network error while uploading"
            : null;
        const errorDetail = serverMessage || "Upload failed";
        uploadResults.failed.push(
          `${file.name} (${timeoutMessage || transportMessage || errorDetail})`,
        );
      } finally {
        payloadForUpload = null;
      }
    }

    // Provide comprehensive feedback based on results
    setLoading(false);

    if (uploadResults.successful.length > 0) {
      let successMsg = `Successfully uploaded ${uploadResults.successful.length} of ${uploadResults.totalAttempted} image(s)`;
      if (uploadResults.successful.length === 1) {
        successMsg += `: ${uploadResults.successful[0]}`;
      }
      successMsg +=
        ". Please return to the desktop app to complete the process.";
      setSuccess(successMsg);
    }

    if (uploadResults.failed.length > 0) {
      const failureMsg = `${uploadResults.failed.length} upload(s) failed: ${uploadResults.failed.join(", ")}`;

      // If we already have validation errors, append upload failures
      if (validationErrors.length > 0) {
        setError((prev) => `${prev}\n${failureMsg}`);
      } else {
        setError(failureMsg);
      }
    }

    // If all uploads failed after validation
    if (
      uploadResults.successful.length === 0 &&
      uploadResults.failed.length > 0
    ) {
      if (!validationErrors.length) {
        setError(
          `All ${uploadResults.totalAttempted} upload(s) failed: ${uploadResults.failed.join(", ")}`,
        );
      }
    }

    e.target.value = "";
  };

  return (
    <div className='mobile-upload-container'>
      <div
        className='bg-white'
        style={{ borderBottom: "0.3rem solid #e3803b" }}
      >
        <img
          alt='Spectra Plasmonics Logo'
          src={spectraLogo}
          style={{ maxWidth: "20rem", width: "30vw", minWidth: "14rem" }}
          className='d-inline-block align-top my-2 mx-2'
        />
      </div>
      <div className='mobile-upload-header'>
        <h1>Upload Sample Photos</h1>
        <p className='session-info'>Session ID: {sessionId}</p>
        <p className='image-count-info'>
          Images: {currentImageCount}/{maxImages}
        </p>
        {(isDesktopConnected || sessionExpired) && (
          <p
            className='session-timer fw-semibold'
            style={{
              color: "#555",
              fontSize: "0.9rem",
              marginTop: "0.5rem",
            }}
          >
            Time remaining: {formatTimeRemaining(timeRemaining)}
          </p>
        )}
      </div>

      <div className='mobile-upload-content'>
        {topStatusAlert && (
          <div className={`alert alert-${topStatusAlert.variant}`}>
            <strong>{topStatusAlert.title}</strong> {topStatusAlert.body}
            {topStatusAlert.footer && (
              <div style={{ marginTop: "0.75rem" }}>
                {topStatusAlert.footer}
              </div>
            )}
          </div>
        )}

        {/* PIN Verification Screen */}
        {!pinVerified && !sessionExpired && (
          <div
            className='pin-verification-container'
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <div className='pin-instructions' style={{ textAlign: "center" }}>
              <h3>
                <i className='bi bi-lock-fill'></i> Verify Your Device
              </h3>
              <p>
                Please enter the 6-digit PIN displayed on the desktop app to
                continue.
              </p>
            </div>

            <form
              onSubmit={handleVerifyPin}
              className='pin-form'
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              <div className='pin-input-wrapper'>
                <input
                  type='text'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  maxLength='6'
                  value={pinInput}
                  onChange={handlePinInputChange}
                  placeholder='000000'
                  className='pin-input'
                  disabled={verifyingPin}
                  style={{
                    fontSize: "2rem",
                    textAlign: "center",
                    letterSpacing: "0.5rem",
                    padding: "1rem",
                    border: pinError
                      ? "2px solid #dc3545"
                      : "2px solid #dee2e6",
                    borderRadius: "8px",
                    width: "100%",
                    maxWidth: "300px",
                    fontFamily: "monospace",
                  }}
                />
              </div>

              {pinError && (
                <div
                  className='alert alert-error'
                  style={{ marginTop: "1rem" }}
                >
                  <strong>Error:</strong> {pinError}
                </div>
              )}

              <button
                type='submit'
                className='btn btn-primary'
                disabled={verifyingPin || pinInput.length !== 6}
                style={{
                  marginTop: "1rem",
                  padding: "0.75rem 2rem",
                  fontSize: "1.1rem",
                  width: "100%",
                  maxWidth: "300px",
                }}
              >
                {verifyingPin ? "Verifying..." : "Verify PIN"}
              </button>
            </form>

            <div
              className='pin-help-text'
              style={{
                marginTop: "2rem",
                padding: "1rem",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                fontSize: "0.9rem",
                color: "#6c757d",
                textAlign: "center",
                width: "100%",
                maxWidth: "500px",
              }}
            >
              <p style={{ marginBottom: "0.5rem" }}>
                <strong>Can't find the PIN?</strong>
              </p>
              <p style={{ marginBottom: "0" }}>
                The PIN is displayed on the desktop app that generated the QR
                code. If you don't see it, ask the person who shared the QR code
                with you.
              </p>
            </div>
          </div>
        )}

        {/* Upload Interface - Only shown after PIN verification */}
        {pinVerified && !sessionExpired && (
          <>
            {/* Camera Input */}
            <div className='input-section'>
              <label
                htmlFor='camera-input'
                className='camera-input-label'
                role='button'
                aria-disabled={
                  loading ||
                  currentImageCount >= maxImages ||
                  sessionExpired ||
                  !isDesktopConnected
                }
                tabIndex={
                  loading ||
                  currentImageCount >= maxImages ||
                  sessionExpired ||
                  !isDesktopConnected
                    ? -1
                    : 0
                }
              >
                <i
                  className='bi bi-camera-fill camera-icon'
                  aria-label='Camera'
                ></i>
                <span>
                  {currentImageCount >= maxImages
                    ? "Maximum Images Reached"
                    : "Take a Photo"}
                </span>
              </label>
              <input
                id='camera-input'
                type='file'
                accept='image/*'
                capture='camera'
                multiple
                onChange={handleFileSelect}
                disabled={
                  loading ||
                  currentImageCount >= maxImages ||
                  sessionExpired ||
                  !isDesktopConnected
                }
                className='camera-input'
                aria-label='Take photos from camera'
              />
            </div>

            {/* Upload Alternative */}
            <div className='input-section'>
              <label
                htmlFor='gallery-input'
                className='gallery-input-label'
                role='button'
                aria-disabled={
                  loading ||
                  currentImageCount >= maxImages ||
                  sessionExpired ||
                  !isDesktopConnected
                }
                tabIndex={
                  loading ||
                  currentImageCount >= maxImages ||
                  sessionExpired ||
                  !isDesktopConnected
                    ? -1
                    : 0
                }
              >
                <i
                  className='bi bi-images gallery-icon'
                  aria-label='Gallery'
                ></i>
                <span>
                  {currentImageCount >= maxImages
                    ? "Maximum Images Reached"
                    : "Or Choose from Gallery"}
                </span>
              </label>
              <input
                id='gallery-input'
                type='file'
                accept='image/*'
                multiple
                onChange={handleFileSelect}
                disabled={
                  loading ||
                  currentImageCount >= maxImages ||
                  sessionExpired ||
                  !isDesktopConnected
                }
                className='gallery-input'
                aria-label='Choose photos from gallery'
              />
            </div>

            {/* Loading Indicator */}
            {loading && (
              <div className='loading-indicator'>
                <div className='spinner'></div>
                <p>Uploading...</p>
              </div>
            )}

            {/* Error Message */}
            {error && !showDisconnectAlert && (
              <div className='alert alert-error'>
                <strong>Error:</strong> {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className='alert alert-success'>
                <strong>Success:</strong> {success}
              </div>
            )}

            {/* Instructions */}
            <div className='instructions'>
              <h3>Instructions</h3>
              <ol>
                <li>Tap "Take a Photo" to open your camera</li>
                <li>
                  Take clear photos of your sample (up to {maxImages} images,{" "}
                  {maxImages - currentImageCount} remaining)
                </li>
                <li>
                  The desktop app will be notified when photos are uploaded
                </li>
                <li>You can upload more photos or close this window</li>
                <li>
                  Remember to return to the desktop app to complete the image
                  upload process
                </li>
              </ol>
            </div>
          </>
        )}
      </div>

      <div className='mobile-upload-footer'>
        <p>
          Keep this window open to receive confirmation from the desktop app
        </p>
      </div>
    </div>
  );
};

export default MobileUpload;
